import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import '../ui/dropdown-menu.js';

/**
 * Settings panel with piano toggle, show-next-beat toggle, and volume slider.
 * Composes <dropdown-menu> internally.
 *
 * @fires setting-change - Fired when a setting changes (detail: { key: string, value: boolean | number })
 */
@customElement('settings-panel')
export class SettingsPanel extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
            }

            dropdown-menu {
                --dropdown-min-width: 200px;
                --dropdown-padding: 12px;
            }

            .settings-btn {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                background: transparent;
                border: 1px solid var(--border-color, #475569);
                border-radius: 6px;
                color: var(--text-secondary, #cbd5e1);
                font-size: 0.85rem;
                font-weight: 500;
                padding: 0.5rem 0.75rem;
                cursor: pointer;
                transition: all 0.2s;
                white-space: nowrap;
            }

            .settings-btn:hover {
                background: var(--surface-light, rgba(255, 255, 255, 0.1));
                color: var(--text-primary, #f1f5f9);
            }

            .settings-item {
                display: flex;
                align-items: center;
                gap: 8px;
                color: #fff;
                font-size: 0.9rem;
                cursor: pointer;
            }

            .settings-item input[type="checkbox"] {
                width: 16px;
                height: 16px;
                cursor: pointer;
            }

            .settings-item.volume-control {
                gap: 12px;
            }

            .volume-icon-btn {
                font-size: 1rem;
                min-width: 20px;
                background: transparent;
                border: none;
                padding: 0;
                cursor: pointer;
                color: inherit;
                line-height: 1;
                border-radius: 4px;
            }

            .volume-icon-btn:hover {
                background: var(--surface-light, rgba(255, 255, 255, 0.1));
            }

            .volume-icon-btn:focus-visible {
                outline: 2px solid var(--primary-color, #2563eb);
                outline-offset: 1px;
            }

            .volume-slider {
                flex: 1;
                height: 6px;
                -webkit-appearance: none;
                appearance: none;
                background: #555;
                border-radius: 3px;
                cursor: pointer;
            }

            .volume-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                width: 16px;
                height: 16px;
                background: #fff;
                border-radius: 50%;
                cursor: pointer;
            }

            .volume-slider::-moz-range-thumb {
                width: 16px;
                height: 16px;
                background: #fff;
                border-radius: 50%;
                cursor: pointer;
                border: none;
            }

        `,
    ];

    @property({ type: String })
    prefix: 'conductor' | 'player' = 'conductor';

    @property({ type: Boolean, attribute: 'show-next-beat' })
    showNextBeat = true;

    @property({ type: Number })
    volume = 100;

    @property({ type: Boolean, attribute: 'piano-visible' })
    pianoVisible = true;

    @property({ type: Boolean, attribute: 'pitch-name-noteheads' })
    pitchNameNoteheads = false;

    @property({ type: Boolean, attribute: 'fingering-hints' })
    fingeringHints = true;

    @property({ type: Boolean, attribute: 'wrong-note-sound' })
    wrongNoteSound = true;

    /** Dynamics warnings + dynamics score penalty. Defaults OFF — see _loadFromLocalStorage. */
    @property({ type: Boolean, attribute: 'dynamics-feedback' })
    dynamicsFeedback = false;

    // Volume level to restore on unmute. Captured whenever the user sets a
    // non-zero volume; consumed when the user clicks the icon while muted.
    private _volumeBeforeMute = 100;

    connectedCallback(): void {
        super.connectedCallback();
        this._loadFromLocalStorage();
    }

    private _loadFromLocalStorage(): void {
        const savedNextBeat = localStorage.getItem(`${this.prefix}ShowNextBeatNotes`);
        this.showNextBeat = savedNextBeat !== 'false'; // default true

        const savedPitchNames = localStorage.getItem(`${this.prefix}PitchNameNoteheads`);
        this.pitchNameNoteheads = savedPitchNames === 'true'; // default false

        const savedFingering = localStorage.getItem(`${this.prefix}FingeringHints`);
        this.fingeringHints = savedFingering !== 'false'; // default true

        const savedWrongNoteSound = localStorage.getItem(`${this.prefix}WrongNoteSound`);
        this.wrongNoteSound = savedWrongNoteSound !== 'false'; // default true

        // Note the inverted idiom: this setting defaults OFF, so it opts IN on
        // 'true' rather than out on 'false'. Dynamics judging is keyboard-dependent
        // and applies a score penalty, so it must not switch itself on for users
        // whose existing performance history was scored without it.
        const savedDynamics = localStorage.getItem(`${this.prefix}DynamicsFeedback`);
        this.dynamicsFeedback = savedDynamics === 'true'; // default false

        const savedVolume = localStorage.getItem(`${this.prefix}Volume`);
        this.volume = savedVolume !== null ? parseFloat(savedVolume) : 100;
        this._volumeBeforeMute = this.volume > 0 ? this.volume : 100;
    }

    private _onPianoToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.pianoVisible = checked;
        this._fireSetting('pianoVisible', checked);
    }

    private _onNextBeatToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.showNextBeat = checked;
        localStorage.setItem(`${this.prefix}ShowNextBeatNotes`, String(checked));
        this._fireSetting('showNextBeat', checked);
    }

    private _onPitchNameNoteheadsToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.pitchNameNoteheads = checked;
        localStorage.setItem(`${this.prefix}PitchNameNoteheads`, String(checked));
        this._fireSetting('pitchNameNoteheads', checked);
    }

    private _onFingeringHintsToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.fingeringHints = checked;
        localStorage.setItem(`${this.prefix}FingeringHints`, String(checked));
        this._fireSetting('fingeringHints', checked);
    }

    private _onWrongNoteSoundToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.wrongNoteSound = checked;
        localStorage.setItem(`${this.prefix}WrongNoteSound`, String(checked));
        this._fireSetting('wrongNoteSound', checked);
    }

    private _onDynamicsFeedbackToggle(e: Event): void {
        const checked = (e.target as HTMLInputElement).checked;
        this.dynamicsFeedback = checked;
        localStorage.setItem(`${this.prefix}DynamicsFeedback`, String(checked));
        this._fireSetting('dynamicsFeedback', checked);
    }

    private _onVolumeInput(e: Event): void {
        const vol = parseFloat((e.target as HTMLInputElement).value);
        this._setVolume(vol);
    }

    private _onVolumeIconClick(e: Event): void {
        // Keep the dropdown open so the user sees the icon state flip.
        // <dropdown-menu> auto-closes on any BUTTON click inside via its
        // _onMenuClick handler — stop propagation to prevent that.
        e.stopPropagation();
        // Toggle: if audible, remember current level and mute; if muted, restore.
        if (this.volume > 0) {
            this._volumeBeforeMute = this.volume;
            this._setVolume(0);
        } else {
            this._setVolume(this._volumeBeforeMute > 0 ? this._volumeBeforeMute : 100);
        }
    }

    private _setVolume(vol: number): void {
        this.volume = vol;
        if (vol > 0) this._volumeBeforeMute = vol;
        localStorage.setItem(`${this.prefix}Volume`, String(vol));
        this._fireSetting('volume', vol);
    }

    private _fireSetting(key: string, value: boolean | number): void {
        this.dispatchEvent(
            new CustomEvent('setting-change', {
                bubbles: true,
                composed: true,
                detail: { key, value },
            })
        );
    }

    render() {
        return html`
            <dropdown-menu position="top-right">
                <button slot="trigger" class="settings-btn">
                    <span>${'\u2699\uFE0F'}Settings</span>
                </button>
                <label class="settings-item">
                    <input
                        type="checkbox"
                        .checked=${this.pianoVisible}
                        @change=${this._onPianoToggle}
                    >
                    <span>${'\uD83C\uDFB9'} Show piano keyboard</span>
                </label>
                <label class="settings-item">
                    <input
                        type="checkbox"
                        .checked=${this.showNextBeat}
                        @change=${this._onNextBeatToggle}
                    >
                    <span>Show next beat's notes</span>
                </label>
                <label class="settings-item">
                    <input
                        type="checkbox"
                        .checked=${this.pitchNameNoteheads}
                        @change=${this._onPitchNameNoteheadsToggle}
                    >
                    <span>Pitch-name noteheads</span>
                </label>
                <label class="settings-item">
                    <input
                        type="checkbox"
                        .checked=${this.fingeringHints}
                        @change=${this._onFingeringHintsToggle}
                    >
                    <span>Fingering hints</span>
                </label>
                <label class="settings-item">
                    <input
                        type="checkbox"
                        .checked=${this.wrongNoteSound}
                        @change=${this._onWrongNoteSoundToggle}
                    >
                    <span>${'🔔'} Wrong-note sound</span>
                </label>
                ${this.prefix === 'conductor' ? html`
                    <label class="settings-item">
                        <input
                            type="checkbox"
                            .checked=${this.dynamicsFeedback}
                            @change=${this._onDynamicsFeedbackToggle}
                        >
                        <span>${'🎹'} Dynamics feedback</span>
                    </label>
                ` : ''}
                <div class="settings-item volume-control">
                    <button
                        type="button"
                        class="volume-icon-btn"
                        title=${this.volume > 0 ? 'Mute' : 'Unmute'}
                        aria-label=${this.volume > 0 ? 'Mute' : 'Unmute'}
                        @click=${this._onVolumeIconClick}
                    >${this.volume > 0 ? '\uD83D\uDD0A' : '\uD83D\uDD07'}</button>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        .value=${String(this.volume)}
                        class="volume-slider"
                        @input=${this._onVolumeInput}
                    >
                </div>
            </dropdown-menu>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'settings-panel': SettingsPanel;
    }
}
